// =============================================
// SEO Meta Generator — generuje seo_title + meta_description per node
// PRZED publikacją do WP. Output zapisywany w thematic_node_content.{seo_title, meta_description}.
//
// Flow:
//   1. Fetch node + content + KG entities (preferred source) + outline (H2)
//   2. Load prompt z thematic_prompts (step=seo_meta_generation)
//   3. Call OpenAI gpt-4.1 ze Structured Outputs (strict JSON)
//   4. Walidacja długości (title 30-70, description 120-170) + ostrzeżenia
//   5. Save do DB
//
// Wiring w publish:
//   publishQueueService czyta `content.seo_title` + `content.meta_description`
//   automatycznie — jeśli są w DB, używa ich jako title WP post + meta_description.
//   Brak ich = fallback do node.title + buildDescription z final_html (jak dotąd).
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { callLLMForStage, parseLLMJSON } from "@/services/llm";
import { loadPrompt, fillTemplate } from "@/services/llm/promptLoader";
import { logPublic, logDebug } from "@/services/contentEngine/contentLogger";

export interface SeoMetaResult {
  seo_title: string;
  meta_description: string;
  rationale: string;
  /** Walidacje (długość, halucynacje) — non-blocking */
  warnings: string[];
  cost_usd: number;
  model_used: string;
}

interface NodeContext {
  primary_entity: string;
  canonical_question: string;
  document_role: string;
  h1_title: string;
  market_keyword: string;
  h2_headers: string;
  top_entities: string;
  article_excerpt: string;
  article_lead: string;
  seed_category: string;
}

const FALLBACK_PROMPT = `Wygeneruj SEO title (30-70 znaków, sentence case, zawiera primary_entity verbatim) i meta_description (120-170 znaków, 2-3 zdania, zaczyna primary_entity, zawiera 1 verifiable detail) dla artykułu po polsku.

Primary entity: {{primary_entity}}
Canonical question: {{canonical_question}}
Document role: {{document_role}}
H1: {{h1_title}}
Keyword: {{market_keyword}}
H2 headers: {{h2_headers}}
Top entities: {{top_entities}}
Article excerpt: {{article_excerpt}}

Zwróć STRICT JSON: { "seo_title": "...", "meta_description": "...", "rationale": "..." }`;

function stripHtmlSimple(html: string): string {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function extractH2Headers(html: string): string[] {
  const matches = html.matchAll(/<h2\b[^>]*>([\s\S]*?)<\/h2>/gi);
  const headers: string[] = [];
  for (const m of matches) {
    const text = stripHtmlSimple(m[1]);
    if (text) headers.push(text);
  }
  return headers;
}

function extractFirstParagraph(html: string): string {
  const match = html.match(/<p\b[^>]*>([\s\S]*?)<\/p>/i);
  if (!match) return "";
  return stripHtmlSimple(match[1]);
}

function buildArticleExcerpt(finalHtml: string, maxChars: number = 1500): string {
  // Take consecutive <p> aż do ~maxChars (intro + lead + meat of the article).
  const paragraphs = [...finalHtml.matchAll(/<p\b[^>]*>([\s\S]*?)<\/p>/gi)].map((m) =>
    stripHtmlSimple(m[1]),
  );
  let out = "";
  for (const p of paragraphs) {
    if (!p) continue;
    if (out.length + p.length + 1 > maxChars) {
      out += " " + p.slice(0, maxChars - out.length);
      break;
    }
    out += (out ? " " : "") + p;
    if (out.length >= maxChars) break;
  }
  return out.slice(0, maxChars).trim();
}

async function fetchNodeContext(nodeId: string): Promise<NodeContext | null> {
  const { data: node } = await (supabase as any)
    .from("thematic_nodes")
    .select("title, canonical_question, document_role, primary_entity_raw, map_id, seed_id")
    .eq("id", nodeId)
    .maybeSingle();
  if (!node) return null;

  const { data: content } = await (supabase as any)
    .from("thematic_node_content")
    .select("final_html, engine_debug")
    .eq("node_id", nodeId)
    .maybeSingle();
  if (!content?.final_html) return null;

  // KISS: LLM bazuje na H1 + article_excerpt + h2_headers. To wystarczy.
  // primary_entity = H1 (zawiera kluczowe encje wybrane przez Etap 1). Gdy H1 > 40 chars,
  // prompt mówi LLM żeby użył skrótu z top_entities — i tam mamy KG-quality entities.
  // top_entities: KG (preferred) → engine_debug (fallback) → "(brak)".
  let topEntitiesStr = "";
  try {
    const { data: kg } = await (supabase as any)
      .from("content_knowledge_graphs")
      .select("kg_data")
      .eq("node_id", nodeId)
      .maybeSingle();
    const entities = (kg?.kg_data as {
      entities?: Array<{ entity_name?: string; name?: string; strength?: number }>;
    } | null)?.entities;
    if (Array.isArray(entities) && entities.length > 0) {
      topEntitiesStr = entities
        .map((e) => ({
          name: typeof e.entity_name === "string" ? e.entity_name : typeof e.name === "string" ? e.name : "",
          strength: typeof e.strength === "number" ? e.strength : 0,
        }))
        .filter((e) => e.name.length > 0)
        .sort((a, b) => b.strength - a.strength)
        .slice(0, 8)
        .map((e, idx) => `${idx + 1}. ${e.name}`)
        .join("\n");
    }
  } catch {
    // ignore — fallback do engine_debug
  }
  if (!topEntitiesStr) {
    const dbgTop = (content.engine_debug as { input_context?: { top_entities?: string[] } } | null)?.input_context
      ?.top_entities;
    if (Array.isArray(dbgTop) && dbgTop.length > 0) {
      topEntitiesStr = dbgTop.map((e, idx) => `${idx + 1}. ${e}`).join("\n");
    }
  }
  if (!topEntitiesStr) topEntitiesStr = "(brak danych)";

  const h2Arr = extractH2Headers(content.final_html);
  const h2Str = h2Arr.length > 0 ? h2Arr.map((h, idx) => `${idx + 1}. ${h}`).join("\n") : "(brak H2)";

  const h1Title = node.title || "";

  // Seed/category — TYLKO jako articleSection context dla LLM (NIE jako temat).
  // Prompt jawnie zakazuje używać tej nazwy jako głównej frazy w title.
  let seedCategory = "(brak)";
  if (node.seed_id) {
    try {
      const { data: seed } = await (supabase as any)
        .from("thematic_seeds")
        .select("name, hub_title")
        .eq("id", node.seed_id)
        .maybeSingle();
      if (seed) {
        seedCategory = (seed as { hub_title?: string; name?: string }).hub_title
          || (seed as { name?: string }).name
          || "(brak)";
      }
    } catch {
      // ignore
    }
  }

  const lead = extractFirstParagraph(content.final_html);

  return {
    primary_entity: h1Title, // legacy var, kept dla backward compat ze starym prompt v1 wariantem
    canonical_question: node.canonical_question || h1Title,
    document_role: node.document_role || "definition",
    h1_title: h1Title,
    market_keyword: h1Title, // legacy
    h2_headers: h2Str,
    top_entities: topEntitiesStr,
    article_excerpt: buildArticleExcerpt(content.final_html, 1500),
    article_lead: lead || "(brak lead)",
    seed_category: seedCategory,
  };
}

/**
 * Hard truncate gdy LLM przekroczy max — word boundary, sentence-aware.
 * Title max 70, description max 170. Min limity (30/120) tylko ostrzeżenie — nie da się dorobić tekstu.
 */
function truncateToLimit(text: string, maxChars: number): string {
  const trimmed = text.trim();
  if (trimmed.length <= maxChars) return trimmed;
  // Spróbuj uciąć na ostatniej kropce w zakresie [maxChars*0.7, maxChars].
  const slice = trimmed.slice(0, maxChars);
  const lastSentence = Math.max(slice.lastIndexOf(". "), slice.lastIndexOf("? "), slice.lastIndexOf("! "));
  if (lastSentence > maxChars * 0.7) {
    return slice.slice(0, lastSentence + 1).trim();
  }
  // Fallback: word boundary cut + brak ellipsis (description nie powinien mieć "...").
  const lastSpace = slice.lastIndexOf(" ");
  if (lastSpace > maxChars * 0.7) {
    return slice.slice(0, lastSpace).replace(/[\s.,;:–—\-?!]+$/, "").trim();
  }
  return slice.replace(/[\s.,;:–—\-?!]+$/, "").trim();
}

function validate(result: { seo_title: string; meta_description: string }): string[] {
  const warns: string[] = [];
  const titleLen = result.seo_title.length;
  if (titleLen < 30) warns.push(`seo_title length ${titleLen} poniżej minimum 30`);
  if (titleLen > 70) warns.push(`seo_title length ${titleLen} powyżej max 70 (truncated)`);
  if (/[A-ZĄĆĘŁŃÓŚŹŻ]{4,}/.test(result.seo_title)) warns.push("seo_title zawiera CAPS LOCK fragment");
  if (result.seo_title.endsWith("...") || result.seo_title.endsWith("…")) warns.push("seo_title kończy ellipsis");
  if (/[\u{1F300}-\u{1FAFF}]/u.test(result.seo_title)) warns.push("seo_title zawiera emoji");

  const descLen = result.meta_description.length;
  if (descLen < 120) warns.push(`meta_description length ${descLen} poniżej minimum 120`);
  if (descLen > 170) warns.push(`meta_description length ${descLen} powyżej max 170 (truncated)`);
  if (/[\u{1F300}-\u{1FAFF}]/u.test(result.meta_description)) warns.push("meta_description zawiera emoji");

  return warns;
}

const JSON_SCHEMA = {
  type: "object",
  additionalProperties: false,
  properties: {
    seo_title: { type: "string" },
    meta_description: { type: "string" },
    rationale: { type: "string" },
  },
  required: ["seo_title", "meta_description", "rationale"],
};

export async function generateSeoMeta(nodeId: string): Promise<SeoMetaResult> {
  const ctx = await fetchNodeContext(nodeId);
  if (!ctx) {
    throw new Error(`Node ${nodeId} brak final_html lub node nie istnieje`);
  }

  const loaded = await loadPrompt("seo_meta_generation");
  const template = loaded?.template || FALLBACK_PROMPT;

  const prompt = fillTemplate(template, {
    primary_entity: ctx.primary_entity,
    canonical_question: ctx.canonical_question,
    document_role: ctx.document_role,
    h1_title: ctx.h1_title,
    market_keyword: ctx.market_keyword,
    h2_headers: ctx.h2_headers,
    top_entities: ctx.top_entities,
    article_excerpt: ctx.article_excerpt,
    article_lead: ctx.article_lead,
    seed_category: ctx.seed_category,
  });

  logDebug("SEO_META", `Generuję SEO meta dla node ${nodeId} (primary_entity="${ctx.primary_entity}")`);

  // model_id z DB steruje modelem (od 2026-08-23) — wcześniej callLLMForStage brał
  // wyłącznie DEFAULT_STAGE_MODELS. Zamrożona wartość = dotychczasowy default (gpt-4.1).
  const modelOverride = loaded?.modelId
    ? { seo_meta_generation: { provider: (loaded.modelId.startsWith("claude") ? "claude" : "openai") as "claude" | "openai", model: loaded.modelId } }
    : undefined;

  const response = await callLLMForStage("seo_meta_generation", prompt, {
    nodeId,
    step: "seo_meta_generation",
    temperature: loaded?.temperature ?? 0.3,
    maxTokens: 600,
    jsonSchema: JSON_SCHEMA,
  }, modelOverride);

  const parsed = parseLLMJSON<{ seo_title: string; meta_description: string; rationale: string }>(
    response.text,
  );
  if (!parsed.seo_title || !parsed.meta_description) {
    throw new Error("LLM zwrócił pustą odpowiedź (seo_title lub meta_description brak)");
  }

  // Hard truncate gdy LLM przekroczy limity (zwłaszcza description częsty 200+ chars).
  const seo_title = truncateToLimit(parsed.seo_title, 70);
  const meta_description = truncateToLimit(parsed.meta_description, 170);
  const rationale = (parsed.rationale || "").trim();
  const warnings = validate({ seo_title, meta_description });

  logPublic(
    "SEO_META",
    `OK node ${nodeId}: title=${seo_title.length}ch, desc=${meta_description.length}ch, $${(response.costUsd ?? 0).toFixed(4)}${warnings.length > 0 ? `, warns=${warnings.length}` : ""}`,
  );

  // Save do DB
  const { error: saveErr } = await (supabase as any)
    .from("thematic_node_content")
    .update({
      seo_title,
      meta_description,
      updated_at: new Date().toISOString(),
    })
    .eq("node_id", nodeId);

  if (saveErr) {
    throw new Error(`DB save failed: ${saveErr.message || saveErr}`);
  }

  return {
    seo_title,
    meta_description,
    rationale,
    warnings,
    cost_usd: response.costUsd ?? 0,
    model_used: response.modelUsed,
  };
}

export interface BatchSeoMetaProgress {
  nodeId: string;
  status: "ok" | "error";
  error?: string;
  result?: SeoMetaResult;
}

/**
 * Batch generate dla wszystkich ready nodów bez seo_title/meta_description.
 * onProgress wywoływane po każdym node.
 */
export async function batchGenerateSeoMeta(
  mapId: string,
  options: { onlyMissing?: boolean; onProgress?: (p: BatchSeoMetaProgress) => void } = {},
): Promise<{ processed: number; failed: number }> {
  const { onlyMissing = true, onProgress } = options;
  let query = (supabase as any)
    .from("thematic_node_content")
    .select("node_id, seo_title, meta_description, engine_stage, thematic_nodes!inner(id, map_id, title)")
    .eq("thematic_nodes.map_id", mapId)
    .eq("engine_stage", "ready");
  if (onlyMissing) {
    query = query.or("seo_title.is.null,meta_description.is.null");
  }
  const { data: rows, error } = await query;
  if (error) throw error;
  const nodeIds = (rows || []).map((r: { node_id: string }) => r.node_id);

  let processed = 0;
  let failed = 0;
  for (const nodeId of nodeIds) {
    try {
      const result = await generateSeoMeta(nodeId);
      processed++;
      onProgress?.({ nodeId, status: "ok", result });
    } catch (e) {
      failed++;
      const msg = e instanceof Error ? e.message : String(e);
      onProgress?.({ nodeId, status: "error", error: msg });
    }
  }
  return { processed, failed };
}
